import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ChatiaDialogActions from "../ChatiaDialogActions";
import { CloseXIcon } from "../ChatiaIcons";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  dialogPaper: {
    ...(theme.chatiaDS?.dialogPaper || {}),
  },
  header: {
    ...(theme.chatiaDS?.header || {}),
    flexShrink: 0,
  },
  headerTitle: {
    ...(theme.chatiaDS?.headerTitle || {}),
  },
  closeButton: {
    backgroundColor: theme.chatiaDS?.closeButton?.backgroundColor || "rgba(255,255,255,0.08)",
    color: theme.chatiaDS?.closeButton?.color || "#ffffff",
    transition: theme.chatiaDS?.closeButton?.transition || "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.chatiaDS?.closeButtonHover?.backgroundColor || "rgba(255,255,255,0.18)",
      color: "#ffffff",
    },
  },
  content: {
    padding: "18px 20px !important",
  },
  textField: {
    width: "100%",
  },
}));

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.transform((value) => (value == null ? "" : value))
		.typeError(i18n.t("flowbuilderModal.validation.required"))
		.min(2, i18n.t("flowbuilderModal.validation.tooShort"))
		.max(50, i18n.t("flowbuilderModal.validation.tooLong"))
		.required(i18n.t("flowbuilderModal.validation.required")),
});

const FlowBuilderModal = ({ open, onClose, flowId, nameWebhook = "", initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);

	const [contact, setContact] = useState({
		name: nameWebhook,
	});

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleClose = () => {
		onClose();
		setContact({
			name: "",
		});
	};

	const handleSaveContact = async values => {
		if(flowId){
			try {
				await api.put("/flowbuilder", {
					name: values.name,
					flowId
				  });
				  onSave(values.name)
				  handleClose()
				toast.success(i18n.t("webhookModal.toasts.update"));
			} catch (err) {
				toastError(err);
			}
		} else {
		try {
			await api.post("/flowbuilder", {
				name: values.name,
			  });
			  onSave(values.name)
			  handleClose()
			toast.success(i18n.t("webhookModal.saveSuccess"));
		} catch (err) {
			toastError(err);
		}
	}
	};
	
	return (
		<div className={classes.root}>
			<Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <div className={classes.header}>
          <Typography className={classes.headerTitle}>
            {flowId
              ? i18n.t("flowbuilderModal.title.edit")
              : i18n.t("flowbuilderModal.title.add")}
          </Typography>
          <IconButton size="small" onClick={handleClose} className={classes.closeButton}>
            <CloseXIcon style={{ fontSize: 18 }} />
          </IconButton>
        </div>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting }) => (
						<Form>
							<DialogContent dividers className={classes.content}>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.name")}
									name="name"
									autoFocus
									defaultValue={contact.name}
									error={Boolean(errors.name)}
									helperText={errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
							
							</DialogContent>
							<ChatiaDialogActions

							  type="submit"

							  disabled={isSubmitting}

							  loading={isSubmitting}

							  saveText={flowId

							    ? `${i18n.t("contactModal.buttons.okEdit")}`

							    : `${i18n.t("contactModal.buttons.okAdd")}`}

							/>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default FlowBuilderModal;
